import type { ClientType, LegalType } from '@shared/documents'
import type { ChangeEvent } from 'react'
import { SectionCard } from './SectionCard'

type PassportOcrSectionsProps = {
  clientType: ClientType
  legalType: LegalType
  isUploadingPassport: boolean
  isMainPassportLoading: boolean
  isRegistrationLoading: boolean
  onMainFileChange: (event: ChangeEvent<HTMLInputElement>) => void
  onRegistrationFileChange: (event: ChangeEvent<HTMLInputElement>) => void
  onClientTypeChange: (clientType: ClientType) => void
  onLegalTypeChange: (legalType: LegalType) => void
}

const PhotoIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="w-8 h-8"
  >
    <title>Photo</title>
    <path
      fillRule="evenodd"
      d="M1.5 6a2.25 2.25 0 0 1 2.25-2.25h16.5A2.25 2.25 0 0 1 22.5 6v12a2.25 2.25 0 0 1-2.25 2.25H3.75A2.25 2.25 0 0 1 1.5 18V6ZM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0 0 21 18v-1.94l-2.69-2.689a1.5 1.5 0 0 0-2.12 0l-.88.879.97.97a.75.75 0 1 1-1.06 1.06l-5.16-5.159a1.5 1.5 0 0 0-2.12 0L3 16.061Zm10.125-7.81a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Z"
      clipRule="evenodd"
    />
  </svg>
)

const LoadingIcon = () => (
  <svg
    className="inline w-7 h-7 text-neutral-tertiary animate-spin fill-brand"
    viewBox="0 0 100 101"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Loading</title>
    <path
      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
      fill="var(--app-accent-soft)"
    />
    <path
      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
      fill="var(--app-accent)"
    />
  </svg>
)

const toggleButtonClassName = (isActive: boolean) =>
  `flex-1 rounded-[22px] px-4 py-2 text-sm font-medium transition ${
    isActive
      ? 'bg-[var(--app-accent)] text-[var(--app-accent-text)]'
      : 'bg-[var(--app-input)] text-[var(--app-muted)]'
  }`

export const PassportOcrSections = ({
  clientType,
  legalType,
  isUploadingPassport,
  isMainPassportLoading,
  isRegistrationLoading,
  onMainFileChange,
  onRegistrationFileChange,
  onClientTypeChange,
  onLegalTypeChange,
}: PassportOcrSectionsProps) => {
  const dropzoneClassName =
    'block rounded-[28px] border border-dashed border-[var(--app-border)] px-5 py-6 transition'

  const titleClassName = 'text-sm font-semibold tracking-tight'

  const descriptionClassName =
    'mt-2 text-sm text-center leading-6 text-[var(--app-muted)]'

  const isIndividual = clientType === 'individual'
  const isOrganization = legalType === 'organization'

  return (
    <SectionCard title="Клиент">
      <div className="flex gap-2 rounded-[22px] bg-[var(--app-input)] p-1">
        <button
          type="button"
          className={toggleButtonClassName(isIndividual)}
          onClick={() => onClientTypeChange('individual')}
        >
          Физ. лицо
        </button>
        <button
          type="button"
          className={toggleButtonClassName(!isIndividual)}
          onClick={() => onClientTypeChange('legal')}
        >
          Юр. лицо
        </button>
      </div>

      {!isIndividual && (
        <div className="mt-2 flex gap-2 rounded-[22px] bg-[var(--app-input)] p-1">
          <button
            type="button"
            className={toggleButtonClassName(!isOrganization)}
            onClick={() => onLegalTypeChange('ip')}
          >
            ИП
          </button>
          <button
            type="button"
            className={toggleButtonClassName(isOrganization)}
            onClick={() => onLegalTypeChange('organization')}
          >
            Организация
          </button>
        </div>
      )}

      {isIndividual && (
        <>
          <label className="block">
            <input
              accept="image/*"
              className="sr-only"
              disabled={isUploadingPassport}
              type="file"
              onChange={onMainFileChange}
            />
            <span
              className={`${dropzoneClassName} ${
                isUploadingPassport
                  ? 'text-[var(--app-muted)]'
                  : 'text-[var(--app-text)] hover:bg-[var(--app-accent-soft)]'
              }`}
            >
              <span className="flex flex-col items-center">
                {isMainPassportLoading ? <LoadingIcon /> : <PhotoIcon />}
                <span className={titleClassName}>
                  {isMainPassportLoading
                    ? 'Распознаем разворот паспорта...'
                    : 'Основной разворот паспорта'}
                </span>
                <span className={descriptionClassName}>
                  Нажмите, чтобы выбрать файл PNG или JPG
                </span>
              </span>
            </span>
          </label>

          <label className="block">
            <input
              accept="image/*"
              className="sr-only"
              disabled={isUploadingPassport}
              type="file"
              onChange={onRegistrationFileChange}
            />
            <span
              className={`${dropzoneClassName} ${
                isUploadingPassport
                  ? 'text-[var(--app-muted)]'
                  : 'text-[var(--app-text)] hover:bg-[var(--app-accent-soft)]'
              }`}
            >
              <span className="flex flex-col items-center">
                {isRegistrationLoading ? <LoadingIcon /> : <PhotoIcon />}
                <span className={titleClassName}>
                  {isRegistrationLoading
                    ? 'Распознаем адрес регистрации...'
                    : 'Страница с адресом регистрации'}
                </span>
                <span className={descriptionClassName}>
                  Нажмите, чтобы выбрать файл PNG или JPG
                </span>
              </span>
            </span>
          </label>
        </>
      )}
    </SectionCard>
  )
}
